import type { ShipmentItem } from '~/types'
import type { InventoryItemTracking } from '~/types/entities/inventoryItemTracking'

/**
 * Union type for shipment items that can be either ShipmentItem or InventoryItemTracking
 */
export type ShipmentItemUnion = ShipmentItem | InventoryItemTracking

export const isInventoryItemTracking = (
  item: ShipmentItemUnion,
): item is InventoryItemTracking =>
  'itemType' in item &&
  (item.itemType === 'SHIPMENT_ITEM' || item.itemType === 'ORDER_ITEM_RECEIPT')

export const isShipmentItem = (item: ShipmentItemUnion): item is ShipmentItem =>
  !isInventoryItemTracking(item)

/**
 * Gets the normalized store key for an inventory item tracking DTO.
 * Uses prefixed IDs to avoid collisions between shipmentItemId and receiptId.
 */
export const getInventoryItemTrackingKey = (
  item: InventoryItemTracking,
): string => {
  if (item.itemType === 'ORDER_ITEM_RECEIPT' && item.receiptId) {
    return `ORDER_ITEM_RECEIPT:${item.receiptId}`
  }
  const itemType = item.itemType || 'SHIPMENT_ITEM'
  return `${itemType}:${item.shipmentItemId}`
}

/**
 * Extracts the actual ID from a normalized store key (removes the itemType prefix).
 */
export const extractIdFromKey = (key: string): string => {
  const parts = key.split(':')
  return parts.length > 1 ? parts.slice(1).join(':') : key
}

/**
 * Gets the ID of a shipment item, handling both ShipmentItem and InventoryItemTracking types.
 * For InventoryItemTracking, uses receiptId when itemType is ORDER_ITEM_RECEIPT,
 * otherwise uses shipmentItemId. This returns the actual ID (not the prefixed store key).
 */
export const getItemId = (item: ShipmentItemUnion): string => {
  if (isInventoryItemTracking(item)) {
    if (item.itemType === 'ORDER_ITEM_RECEIPT') {
      return item.receiptId
    }
    return item.shipmentItemId
  }
  return item.id
}

/**
 * Gets a unique prefixed key for any ShipmentItemUnion.
 * Uses prefixed IDs to avoid collisions between shipmentItemId and receiptId.
 * For InventoryItemTracking: returns getInventoryItemTrackingKey result.
 * For ShipmentItem: returns SHIPMENT_ITEM:${id} to ensure keys never collide with receipt keys.
 */
export const getItemKey = (item: ShipmentItemUnion): string => {
  if (isInventoryItemTracking(item)) {
    return getInventoryItemTrackingKey(item)
  }
  return `SHIPMENT_ITEM:${item.id}`
}
